import type {
  IPlatformIdentityServices,
  WindowsLoginResult
} from '@axilon-apps/domains-identity/platform'
import type {
  EntitiesAuthenticationConfigResponse,
  EntitiesOidcAuthMethod
} from '@axilon-apps/domains-identity/types'
import type { IPlatformServicesWebOnly } from '@axilon-apps/domains-identity/web'
import { getIdentityApiUrl } from '@axilon-apps/domains-ontology-ui-state'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'

/**
 * Web implementation of the identity platform services for ontology-ui.
 * Only the identity-facing surface is implemented (compare
 * `domains/axilon-devops/src/shell/web/platform/web-platform-services.ts`,
 * which additionally implements the devops desktop no-ops).
 */

let userManager: UserManager | null = null

const webPlatformServices: IPlatformIdentityServices & IPlatformServicesWebOnly = {
  getOrCreateUserManager: (config?: EntitiesOidcAuthMethod): UserManager | null => {
    if (!userManager && config && config.enabled && config.config) {
      userManager = new UserManager({
        authority: config.config.authority,
        client_id: config.config.client_id,
        client_secret: config.config.client_secret,
        redirect_uri: config.config.redirect_uri,
        scope: config.config.scope,
        userStore: new WebStorageStateStore({ store: window.localStorage })
      })
    }
    return userManager
  },

  windowsAuth: {
    login: async (
      _authConfig: EntitiesAuthenticationConfigResponse
    ): Promise<WindowsLoginResult> => {
      const manager = webPlatformServices.getOrCreateUserManager()
      if (!manager) {
        return { error: { messages: ['OIDC auth not available'] } }
      }
      // Trigger the redirect — the page unloads. Tokens are redeemed on the
      // way back by `OidcAuthProvider`, so there is nothing to return: an
      // empty result means "redirect in flight", not "failed".
      await manager.signinRedirect()
      return {}
    },
    signout: async () => {
      // Unconditional: `removeUser` is a no-op when nothing is stored, and
      // leaving a stored user behind would let the next page load exchange it
      // straight back into a session.
      await webPlatformServices.getOrCreateUserManager()?.removeUser()
    }
  },

  versions: {
    desktopApp: 'web',
    backgroundServices: undefined
  },

  logger: {
    info: console.info,
    warn: console.warn,
    error: console.error
  },

  // Read lazily (not at module scope) so public/config.js has populated
  // window.__ONTOLOGY_CONFIG__ before the first consumer asks for it.
  getApiServerUrl: () => getIdentityApiUrl()
}

export default webPlatformServices
